// src/ekd/KnowledgeSourcesPanel.tsx - Jobs page > Docs: the client's knowledge sources for Enterprise Knowledge.
// Upload a catalog export (Collibra / Alation Excel or CSV) or a business document (PDF / DOCX / TXT). Every Excel
// row and every document passage is tied to the fields it is about; the next "Enterprise Knowledge discovery" run
// gives the agent, for each group of fields, the passages about those fields - and its proposals cite them. The SME
// then reviews the proposals on the Enterprise Knowledge screen. Nothing is applied without review.
import React, { useEffect, useRef, useState } from "react";
import { ekd_listSources, ekd_uploadSource, ekd_deleteSource, ekd_rebuildSources, type KnowledgeSources } from "../api/ekdAPI";

export default function KnowledgeSourcesPanel({ entityId, entityLabel }: { entityId: string; entityLabel?: string; key?: any }) {
  const [data, setData] = useState<KnowledgeSources | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = async () => { try { setData(await ekd_listSources(entityId)); } catch (e: any) { setErr(String(e?.message || e)); } };
  useEffect(() => { load(); }, [entityId]); // eslint-disable-line

  const upload = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setErr(null);
    for (const f of Array.from(files)) {
      setBusy(`Saving ${f.name}…`);
      try { setData(await ekd_uploadSource(entityId, f)); }
      catch (e: any) { setErr(`${f.name}: ${String(e?.response?.data?.detail || e?.message || e)}`); }
    }
    setBusy(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  const remove = async (name: string) => {
    setBusy(`Removing ${name}…`);
    try { setData(await ekd_deleteSource(entityId, name)); } catch (e: any) { setErr(String(e?.message || e)); }
    finally { setBusy(null); }
  };
  const rebuild = async () => {
    setBusy("Re-reading all sources…");
    try { setData(await ekd_rebuildSources(entityId)); } catch (e: any) { setErr(String(e?.message || e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="ks">
      <style>{CSS}</style>
      <div className="ks-head">
        <div>
          <div className="ks-title">Knowledge sources{entityLabel ? ` · ${entityLabel}` : ""}</div>
          <div className="ks-sub">Catalog exports (Collibra / Alation Excel, CSV) and business documents (PDF, DOCX, TXT). Each row / passage is tied to the fields it is about and given to the Enterprise Knowledge agent for those fields; its proposals cite the source and the SME reviews them. Uploading only SAVES a file; it is read when <b>Build Enterprise Knowledge</b> runs, so a wrong file can be removed first.</div>
        </div>
        <div className="ks-acts">
          <input ref={fileRef} type="file" multiple accept=".xlsx,.xls,.csv,.pdf,.docx,.doc,.txt,.md" style={{ display: "none" }}
            onChange={(e: any) => upload(e.target.files)} />
          <button className="ks-btn" disabled={!!busy} onClick={() => fileRef.current?.click()}>⬆ Upload</button>
          <button className="ks-btn out" disabled={!!busy || !(data?.sources || []).length} onClick={rebuild}
            title="Optional preview: read the files now to see passages / fields. The job reads them anyway.">Preview reading</button>
        </div>
      </div>
      {busy && <div className="ks-busy">{busy}</div>}
      {err && <div className="ks-err">{err}</div>}
      {data && (
        <div className="ks-sum">{(data.sources || []).length} source(s) · {data.chunks.toLocaleString()} passages · evidence for {data.fields_with_evidence} field(s){data.built_at ? ` · read ${new Date(data.built_at).toLocaleString()}` : ""}</div>
      )}
      <table className="ks-tbl">
        <thead><tr><th>File</th><th>Type</th><th>Passages</th><th>Tied to fields</th><th>Fields</th><th /></tr></thead>
        <tbody>
          {(data?.sources || []).map((s) => (
            <tr key={s.file_name}>
              <td>{s.file_name}{s.error && <div className="ks-err">{s.error}</div>}
                {s.not_read_yet && <div className="ks-note">Saved - read when Build Enterprise Knowledge runs (nothing is extracted before)</div>}
                {(s.skipped_value_lists || []).length > 0 && <div className="ks-note">Value list(s) left out - codes come from Reference Data (Lookup): {(s.skipped_value_lists || []).join(", ")}</div>}</td>
              <td>{s.type === "table" ? "catalog / table" : s.type || "—"}</td>
              <td>{(s.chunks ?? 0).toLocaleString()}</td>
              <td>{(s.chunks_with_fields ?? 0).toLocaleString()}</td>
              <td>{s.fields ?? 0}</td>
              <td><button className="ks-link" disabled={!!busy} onClick={() => remove(s.file_name)}>remove</button></td>
            </tr>))}
          {!(data?.sources || []).length && <tr><td colSpan={6} className="ks-empty">No source uploaded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const CSS = `
.ks{padding:14px 16px;font-family:inherit}
.ks-head{display:flex;gap:16px;align-items:flex-start;justify-content:space-between}
.ks-title{font-size:16px;font-weight:700;color:#1d2a35}.ks-sub{font-size:12.5px;color:#5b6b78;margin-top:4px;max-width:820px}
.ks-acts{display:flex;gap:8px}
.ks-btn{font-weight:600;font-size:13px;border-radius:8px;padding:7px 14px;cursor:pointer;border:1px solid #A100FF;font-family:inherit;background:#fff;color:#7500C0}
.ks-btn.out{border-color:#cfd6dc;color:#33424f}.ks-btn:disabled{opacity:.45;cursor:default}
.ks-busy{margin-top:10px;font-size:12.5px;background:#f7f2fc;color:#4a1d6e;border-radius:7px;padding:8px 10px}
.ks-err{margin-top:8px;font-size:12.5px;background:#fef2f2;color:#991b1b;border-radius:7px;padding:6px 9px}
.ks-sum{margin-top:12px;font-size:12.5px;color:#33424f}
.ks-tbl{width:100%;border-collapse:collapse;margin-top:8px;font-size:12.5px}
.ks-tbl th,.ks-tbl td{border-bottom:1px solid #eef0f2;padding:7px 8px;text-align:left}
.ks-tbl th{color:#5b6b78;font-weight:600}.ks-empty{color:#8a97a3;text-align:center}
.ks-note{font-size:11.5px;color:#5b6b78;margin-top:3px}
.ks-link{all:unset;cursor:pointer;color:#7500C0;text-decoration:underline;font-size:12px}
`;
